// Wiederkehrende Bausteine der Oberfläche. Die Klassennamen stammen
// unverändert aus der alten App, damit das übernommene CSS greift.

import type { ReactNode } from "react";
import { useEffect, useId, useRef } from "react";
import { eur, pct } from "../formate.ts";
import type { Kennzahlen } from "../typen.ts";

// ------------------------------------------------------------- Kennzahlen

export function Kpi(
  { titel, wert, unten, ton }:
  { titel: string; wert: ReactNode; unten?: ReactNode; ton?: "blue" | "red" | "orange" },
) {
  return (
    <div className="kpi">
      <div className="kpi-label">{titel}</div>
      <div className={`kpi-value ${ton ?? ""}`}>{wert}</div>
      {unten ? <div className={`kpi-sub ${ton === "red" ? "red" : ""}`}>{unten}</div> : null}
    </div>
  );
}

export function Mini(
  { titel, wert, ton }: { titel: string; wert: ReactNode; ton?: "blue" | "red" },
) {
  return (
    <div className="mini">
      <div className="mini-label">{titel}</div>
      <div className={`mini-value ${ton ?? ""}`}>{wert}</div>
    </div>
  );
}

/** Die Kachelreihe, die auf Dashboard und Objektseite gleich aussieht. */
export function KennzahlKacheln({ k, zeigeInvestDetail }: { k: Kennzahlen; zeigeInvestDetail?: boolean }) {
  const ueberBudget = k.sanierungBudget > 0 && k.sanierungIst > k.sanierungBudget;
  return (
    <div className="kpi-grid">
      <Kpi
        titel="Gesamtinvestition"
        wert={eur(k.invest)}
        unten={zeigeInvestDetail
          ? `Kauf ${eur(k.kaufpreis)} · NK ${eur(k.nebenkosten)} · San. ${eur(k.sanierungIst)}`
          : "Kaufpreis + NK + Sanierung + investive Mängel"}
      />
      <Kpi titel="Jahreskaltmiete (Ist)" wert={eur(k.jahreskaltmieteIst)}
        unten={`Potenzial: ${eur(k.jahreskaltmietePotenzial)}`} />
      <Kpi titel="Bruttorendite (Ist)" wert={pct(k.renditeIst)} ton="blue"
        unten={k.renditeIst === null ? "Kaufpreis fehlt" : "Ist-Kaltmiete / Gesamtinvest"} />
      <Kpi titel="Sanierung (Ist)" wert={eur(k.sanierungIst)} ton={ueberBudget ? "red" : undefined}
        unten={`Budget: ${eur(k.sanierungBudget)}${ueberBudget ? " · über Budget" : ""}`} />
      <Kpi titel="Offene Mängel" wert={String(k.maengelOffen)}
        ton={k.maengelOffen > 0 ? "orange" : undefined}
        unten={`geschätzte Kosten: ${eur(k.maengelOffenKosten)}`} />
      <Kpi titel="Inventar-Neubeschaffung" wert={eur(k.inventarNeubeschaffung)}
        unten={k.inventarKritisch > 0 ? `${k.inventarKritisch} kritische Positionen` : "aus Bestandsaufnahmen"}
        ton={k.inventarKritisch > 0 ? "red" : undefined} />
    </div>
  );
}

// -------------------------------------------------------------- Leerzustand

export function Leer({ titel, children }: { titel: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <strong>{titel}</strong>
      {children}
    </div>
  );
}

// ------------------------------------------------------------------ Sheet

/**
 * Formularblatt. Am Desktop mittig, auf dem Telefon von unten hereinfahrend —
 * beides steckt schon im übernommenen CSS.
 *
 * Escape schließt, ein Klick auf den Hintergrund auch. Der Fokus springt beim
 * Öffnen ins erste Feld, damit man auf dem Telefon sofort tippen kann.
 */
export function Sheet(
  { titel, children, fuss, aufSchliessen }:
  { titel: string; children: ReactNode; fuss: ReactNode; aufSchliessen: () => void },
) {
  const blatt = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const beiTaste = (e: KeyboardEvent) => { if (e.key === "Escape") aufSchliessen(); };
    document.addEventListener("keydown", beiTaste);
    const ersteEingabe = blatt.current?.querySelector<HTMLElement>(
      "input:not([type=checkbox]):not([type=file]), select, textarea");
    ersteEingabe?.focus();
    return () => document.removeEventListener("keydown", beiTaste);
  }, [aufSchliessen]);

  return (
    <div className="overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) aufSchliessen(); }}>
      <div className="sheet" ref={blatt} role="dialog" aria-modal="true" aria-label={titel}>
        <div className="sheet-hd">
          <h3>{titel}</h3>
          <button className="x" onClick={aufSchliessen} aria-label="Schließen">✕</button>
        </div>
        <div className="sheet-body">
          {children}
          <div className="sheet-ft">{fuss}</div>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------ Formularfelder

export function Feld(
  { titel, children, voll }: { titel: string; children: ReactNode; voll?: boolean },
) {
  return (
    <label className={`fld ${voll ? "full" : ""}`}>
      <span>{titel}</span>
      {children}
    </label>
  );
}

export function TextFeld(
  { titel, wert, aendern, platzhalter, voll, typ = "text", liste }:
  {
    titel: string; wert: string; aendern: (w: string) => void;
    platzhalter?: string; voll?: boolean; typ?: string; liste?: string[];
  },
) {
  const listenId = useId();
  return (
    <Feld titel={titel} voll={voll}>
      <input
        type={typ}
        value={wert}
        placeholder={platzhalter}
        list={liste ? listenId : undefined}
        step={typ === "number" ? "any" : undefined}
        onChange={(e) => aendern(e.target.value)}
      />
      {liste
        ? <datalist id={listenId}>{liste.map((v) => <option key={v} value={v} />)}</datalist>
        : null}
    </Feld>
  );
}

export function ZahlFeld(
  { titel, wert, aendern, platzhalter, voll }:
  { titel: string; wert: number | string; aendern: (w: string) => void; platzhalter?: string; voll?: boolean },
) {
  return (
    <TextFeld titel={titel} typ="number" voll={voll} platzhalter={platzhalter}
      wert={wert === 0 ? "" : String(wert)} aendern={aendern} />
  );
}

export function WahlFeld<T extends string>(
  { titel, wert, aendern, optionen, voll }:
  {
    titel: string; wert: T; aendern: (w: T) => void;
    optionen: readonly (readonly [T, string])[]; voll?: boolean;
  },
) {
  return (
    <Feld titel={titel} voll={voll}>
      <select value={wert} onChange={(e) => aendern(e.target.value as T)}>
        {optionen.map(([schluessel, text]) => (
          <option key={schluessel} value={schluessel}>{text}</option>
        ))}
      </select>
    </Feld>
  );
}

export function TextflaecheFeld(
  { titel, wert, aendern, zeilen = 3 }:
  { titel: string; wert: string; aendern: (w: string) => void; zeilen?: number },
) {
  return (
    <Feld titel={titel} voll>
      <textarea rows={zeilen} value={wert} onChange={(e) => aendern(e.target.value)} />
    </Feld>
  );
}

export function HakenFeld(
  { titel, wert, aendern }: { titel: string; wert: boolean; aendern: (w: boolean) => void },
) {
  return (
    <label className="check">
      <input type="checkbox" checked={wert} onChange={(e) => aendern(e.target.checked)} />
      {titel}
    </label>
  );
}

/** Aus einem Record die Optionsliste bauen — spart das Abtippen. */
export const ausRecord = <T extends string>(record: Record<T, string>) =>
  (Object.entries(record) as [T, string][]).map(([k, v]) => [k, v] as const);

/** Aus einem Array gleiche Schlüssel und Beschriftung. */
export const ausListe = (werte: readonly string[]) =>
  werte.map((v) => [v, v] as const);
